import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { slidingEmptySlotBackground } from "../../../admin/story/slidingEmptySlotStyle";

const GRID_GAP_PX = 2;
const GLOW_TILE_NUMBERS = new Set([2, 25]);

function cloneLayout(layout, n) {
    if (!Array.isArray(layout) || layout.length !== n) {
        return Array.from({ length: n }, (_, i) => i);
    }
    return layout.map((v) => Number(v));
}

function swapCells(next, a, b) {
    if (a < 0 || b < 0 || a === b) return;
    const tmp = next[a];
    next[a] = next[b];
    next[b] = tmp;
}

function cellOf(next, slice) {
    return next.findIndex((v) => Number(v) === slice);
}

/**
 * Empty at bottom-right; tiles 23–25 in a row for the horizontal slide; tile 2 visible.
 */
function poseConceptLayout(layout, gs) {
    const n = gs * gs;
    const next = cloneLayout(layout, n);
    swapCells(next, cellOf(next, n - 1), n - 1);
    if (gs >= 6) {
        swapCells(next, cellOf(next, 22), 21);
        swapCells(next, cellOf(next, 23), 22);
        swapCells(next, cellOf(next, 24), 23);
        swapCells(next, cellOf(next, 1), 7);
    }
    let solved = 0;
    for (let i = 0; i < n - 1; i += 1) {
        if (Number(next[i]) === i) solved += 1;
    }
    if (solved > n / 2) {
        for (let i = 0; i + 1 < n - 1; i += 2) {
            if (i === 21 || i === 22) continue;
            swapCells(next, i, i + 1);
        }
    }
    return next;
}

function tileBgPosition(sliceIndex, gs) {
    const col = sliceIndex % gs;
    const row = Math.floor(sliceIndex / gs);
    if (gs <= 1) return "0% 0%";
    return `${(col / (gs - 1)) * 100}% ${(row / (gs - 1)) * 100}%`;
}

function TileFace({ imageUrl, gs, sliceIndex, isEmpty }) {
    const col = sliceIndex % gs;
    const row = Math.floor(sliceIndex / gs);
    return (
        <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
            <Box
                component="img"
                src={imageUrl}
                alt=""
                draggable={false}
                referrerPolicy="no-referrer"
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${gs * 100}%`,
                    height: `${gs * 100}%`,
                    maxWidth: "none",
                    objectFit: "fill",
                    transform: `translate(-${(col * 100) / gs}%, -${(row * 100) / gs}%)`,
                    transformOrigin: "top left",
                    userSelect: "none",
                    pointerEvents: "none",
                }}
            />
            {isEmpty ? (
                <Box
                    sx={(theme) => ({
                        position: "absolute",
                        inset: 0,
                        backgroundColor: slidingEmptySlotBackground(theme),
                        pointerEvents: "none",
                    })}
                />
            ) : null}
        </Box>
    );
}

const GLASS_SPOTS = [
    { top: "-8%", left: "-10%", rotate: -16, size: 78, slice: 0, opacity: 0.42 },
    { top: "6%", right: "-12%", rotate: 12, size: 62, slice: 5, opacity: 0.38 },
    { bottom: "14%", left: "-14%", rotate: 10, size: 70, slice: 14, opacity: 0.36 },
    { bottom: "-6%", right: "-10%", rotate: -14, size: 86, slice: 20, opacity: 0.4 },
];

/** Frosted puzzle tiles floating around the phone frame. */
export function ScreenshotGiantGlassTiles({ imageUrl, gridSize }) {
    const gs = Number(gridSize) > 0 ? Number(gridSize) : 6;
    if (!imageUrl) return null;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: "-12% -8%",
                pointerEvents: "none",
                zIndex: 0,
                overflow: "visible",
            }}
        >
            {GLASS_SPOTS.map((spot, i) => (
                <Box
                    key={`glass-${i}`}
                    sx={{
                        position: "absolute",
                        top: spot.top,
                        left: spot.left,
                        right: spot.right,
                        bottom: spot.bottom,
                        width: spot.size,
                        height: spot.size,
                        borderRadius: "10px",
                        overflow: "hidden",
                        opacity: spot.opacity,
                        transform: `rotate(${spot.rotate}deg)`,
                        backgroundImage: `url("${imageUrl}")`,
                        backgroundSize: `${gs * 100}% ${gs * 100}%`,
                        backgroundPosition: tileBgPosition(spot.slice, gs),
                        backgroundRepeat: "no-repeat",
                        border: "1px solid rgba(255,255,255,0.38)",
                        boxShadow:
                            "0 10px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.35)",
                        filter: "saturate(0.85) brightness(1.05)",
                    }}
                />
            ))}
        </Box>
    );
}

/**
 * Concept 04 board: 6×6 tiles with empty slot.
 */
export default function ScreenshotGiantExpertBoard({
    imageUrl,
    gridSize,
    startingLayout,
}) {
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    const layout = useMemo(() => poseConceptLayout(startingLayout, gs), [startingLayout, gs]);
    const emptySlice = n - 1;

    if (!imageUrl || n <= 0 || layout.length !== n) return null;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                display: "grid",
                gridTemplateColumns: `repeat(${gs}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gs}, minmax(0, 1fr))`,
                gap: `${GRID_GAP_PX}px`,
                bgcolor: "rgba(15,23,42,0.92)",
                overflow: "hidden",
            }}
        >
            {layout.map((sliceIndexAtCell, visualCellIdx) => {
                const sliceIndex = Number(sliceIndexAtCell);
                const isEmpty = sliceIndex === emptySlice;
                const tileNumber = sliceIndex + 1;
                const glow = GLOW_TILE_NUMBERS.has(tileNumber);

                return (
                    <Box
                        key={`giant-cell-${visualCellIdx}`}
                        sx={{
                            position: "relative",
                            minWidth: 0,
                            minHeight: 0,
                            overflow: "hidden",
                            borderRadius: "2px",
                            zIndex: glow ? 3 : 1,
                            boxShadow: glow
                                ? "0 0 0 2px rgba(186,230,253,0.95), 0 0 14px rgba(56,189,248,0.85)"
                                : "none",
                        }}
                    >
                        <TileFace
                            imageUrl={imageUrl}
                            gs={gs}
                            sliceIndex={isEmpty ? emptySlice : sliceIndex}
                            isEmpty={isEmpty}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}
